import { supabase } from '@/integrations/supabase/client';
import type { DailyPuzzleStorage } from '../types/puzzle-types';
import { getTodayUTC } from '../utils/date-utils';
import type { Json } from '@/integrations/supabase/types/json';
import { validatePuzzle } from '../validation/puzzle-validation';

// Helper function to check if a JSON value has the structure of a puzzle storage object
function isPuzzleStorage(value: Json): value is any {
  if (!value || typeof value !== 'object') return false;
  
  // Check for expected structure
  const puzzle = value as any;
  return (
    Array.isArray(puzzle.words) &&
    typeof puzzle.correctRule === 'object' &&
    puzzle.correctRule !== null &&
    typeof puzzle.correctRule.id === 'string' &&
    Array.isArray(puzzle.correctOrder) &&
    Array.isArray(puzzle.logicRules) &&
    typeof puzzle.date === 'string'
  );
}

export async function storePuzzleInDB(puzzle: DailyPuzzleStorage) {
  const date = getTodayUTC();
  console.log('Attempting to store puzzle in database for date:', date);
  console.log('Reasoning to store in database:', puzzle.correctRule.reasoning 
    ? puzzle.correctRule.reasoning.substring(0, 50) + '...'
    : 'No reasoning provided');
  
  try {
    // Validate the puzzle data before storage
    const validatedPuzzle = validatePuzzle(puzzle);
    if (!validatedPuzzle) {
      console.error('Invalid puzzle data, cannot store in database');
      throw new Error('Puzzle validation failed');
    }

    // First try to get existing puzzle
    const existingPuzzle = await fetchPuzzleFromDB(date);
    if (existingPuzzle) {
      console.log('Found existing puzzle for date:', date);
      return existingPuzzle;
    }

    // Convert to JSON to ensure compatibility
    const puzzleForStorage = JSON.parse(JSON.stringify(puzzle));

    // If no puzzle exists, try to insert new one
    const { data, error } = await supabase
      .from('daily_puzzles')
      .insert({
        date,
        puzzle: puzzleForStorage
      })
      .select('puzzle')
      .single();

    if (error) {
      // If we get a duplicate key error, try to fetch the existing puzzle
      if (error.code === '23505') {
        console.log('Concurrent insert detected, fetching existing puzzle');
        const existingPuzzle = await fetchPuzzleFromDB(date);
        if (existingPuzzle) {
          return existingPuzzle;
        }
      }
      
      console.error('Failed to store puzzle:', error);
      // Return the original puzzle as fallback
      return puzzle;
    }
    
    console.log('Successfully stored new puzzle in database');
    
    // Type check the puzzle data
    if (!isPuzzleStorage(data.puzzle)) {
      console.error('Stored puzzle has invalid format:', data.puzzle);
      return puzzle;
    }
    
    // Since we verified the structure, we can safely cast
    const typedPuzzle = data.puzzle as unknown as DailyPuzzleStorage;
    return typedPuzzle;
  } catch (error) {
    console.error('Error in storePuzzleInDB:', error);
    // Return the original puzzle as fallback
    return puzzle;
  }
}

export async function fetchPuzzleFromDB(date: string) {
  console.log('Fetching puzzle from database for date:', date);
  
  try {
    const { data: puzzleData, error } = await supabase
      .from('daily_puzzles')
      .select('puzzle')
      .eq('date', date)
      .maybeSingle();

    if (error) {
      console.error('Database error fetching puzzle:', error);
      throw error;
    }

    if (!puzzleData) {
      console.log('No puzzle found in database for date:', date);
      return null;
    }

    console.log('Successfully retrieved puzzle from database');
    
    // Type check the puzzle data
    if (!isPuzzleStorage(puzzleData.puzzle)) {
      console.error('Retrieved puzzle has invalid format:', puzzleData.puzzle);
      return null;
    }
    
    // Check for reasoning in the puzzle data
    console.log('Retrieved puzzle data:', {
      correctRuleName: (puzzleData.puzzle as any).correctRule?.name,
      hasReasoning: !!(puzzleData.puzzle as any).correctRule?.reasoning,
      reasoning: (puzzleData.puzzle as any).correctRule?.reasoning 
        ? ((puzzleData.puzzle as any).correctRule.reasoning.substring(0, 50) + '...') 
        : 'No reasoning provided'
    });
    
    // Since we verified the structure matches what we expect, we can safely cast to DailyPuzzleStorage
    const typedPuzzle = puzzleData.puzzle as unknown as DailyPuzzleStorage;
    return typedPuzzle;
  } catch (error) {
    console.error('Error in fetchPuzzleFromDB:', error);
    // Don't throw here - let the calling code handle the error
    return null;
  }
}
